import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredUser, performLogout } from '../../hooks/useAuth';
import { DEFAULT_MIRROR_ID } from '../../config/mirrors';
import { getScoped, setScoped, type StorageScope } from '../../lib/storage';
import { CabinetConfigProvider } from './CabinetConfigContext';
import CabinetSidebar from './CabinetSidebar';
import { getSessionTheme } from '../../config/themes';
import type { CabinetConfig } from './types';

export default function CabinetShell({ config }: { config: CabinetConfig }) {
  const navigate = useNavigate();
  const user = getStoredUser();

  // Идентичность scope должна быть стабильной: секции держат его в зависимостях эффектов.
  const scope: StorageScope = useMemo(
    () => ({
      mirror: user?.mirror ?? DEFAULT_MIRROR_ID,
      vacancy: config.vacancyId,
      username: user?.username ?? 'anon',
    }),
    [user?.mirror, user?.username, config.vacancyId],
  );

  const validIds = config.sections.map((s) => s.id);
  const [activeSection, setActiveSection] = useState<string>(() => {
    const saved = getScoped(scope, 'active_section');
    return saved && validIds.includes(saved) ? saved : config.defaultSection;
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    setScoped(scope, 'active_section', section);
  };

  const handleLogout = () => {
    performLogout();
    navigate('/', { replace: true });
  };

  const current = config.sections.find((s) => s.id === activeSection) ?? config.sections[0];
  const SectionComponent = current.component;
  const theme = getSessionTheme();

  return (
    <CabinetConfigProvider config={config} scope={scope}>
      <div data-theme={theme} className="min-h-screen bg-background-50 flex">
        <div className="hidden lg:flex">
          <CabinetSidebar
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            userName={user?.name ?? ''}
            onLogout={handleLogout}
          />
        </div>

        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-40 flex">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
            <div className="relative z-50 flex">
              <CabinetSidebar
                activeSection={activeSection}
                onSectionChange={(s) => { handleSectionChange(s); setMobileOpen(false); }}
                userName={user?.name ?? ''}
                onLogout={handleLogout}
              />
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col min-w-0">
          <header className="flex items-center gap-4 px-6 lg:px-8 py-5 border-b border-surface-3 sticky top-0 bg-background-50 z-20">
            <button
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl border border-surface-5 text-foreground-600 hover:text-ink transition-colors cursor-pointer"
              onClick={() => setMobileOpen(true)}
            >
              <i className="ri-menu-line" />
            </button>

            <nav className="flex items-center gap-2 text-sm">
              <button
                onClick={() => navigate('/vacancy')}
                className="text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Кабинет
              </button>
              <span className="text-foreground-300"><i className="ri-arrow-right-s-line" /></span>
              <span className="text-foreground-700">{current.title}</span>
            </nav>

            <div className="ml-auto flex items-center gap-3" />
          </header>

          <main className="flex-1 px-6 lg:px-10 py-8 overflow-y-auto theme-grid">
            {theme === 'industrial' && (
              <p className="eyebrow mb-5"><span>Личный кабинет<span className="hidden sm:inline"> · {config.roleLabel}</span></span></p>
            )}
            <SectionComponent />
          </main>
        </div>
      </div>
    </CabinetConfigProvider>
  );
}
